import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormik } from "formik";
import { useContext, useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import Layout from "../../components/layout/Layout";
import { AuthContext } from "../../state/AuthContext";
import "./AddClientPage.css";
import { ClientContext } from "../../state/ClientContext";
import { US_STATES, US_TIMEZONES } from "../../util/defs";
import LeaveWithoutSavingModal from "../../components/LeaveWithoutSavingModal";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const AddClientPage = () => {
  const [caregivers, setCaregivers] = useState([]);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const { registerClient } = useContext(ClientContext);

  useEffect(() => {
    fetchCaregivers();
  }, []);

  const fetchCaregivers = async () => {
    try {
      const response = await fetch(`${BASE_URL}/caregivers`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCaregivers(data);
      } else {
        console.error("Failed to fetch caregivers");
      }
    } catch (error) {
      console.error("Error fetching caregivers:", error);
    }
  };

  const schemaParams = {
    firstName: yup.string().required("Enter first name."),
    lastName: yup.string().required("Enter last name."),
    email: yup.string().email("Enter valid email.").required("Enter email."),
    phone: yup
      .string()
      .matches(/^\+1\d{10}$/, "Phone number must be in the format +1XXXXXXXXXX")
      .required("Enter valid U.S phone number."),
    idNumber: yup.string().required("Enter ID number."),
    address: yup.string().required("Enter address."),
    city: yup
      .string()
      .matches(
        /^[a-zA-Z ]*$/,
        "City can’t contain any special characters or numbers."
      )
      .required("Enter city."),
    state: yup.string().required("Enter state."),
    zipCode: yup
      .string()
      .matches(
        /^\d{5}$/,
        "ZIP code must be 5 digits and can’t contain any special characters or letters."
      )
      .required("Enter ZIP code."),
    timezone: yup.string().required("Select timezone."),
  };

  if (currentUser?.isSuperAdmin) {
    schemaParams.caregiverId = yup
      .array(yup.string())
      .min(1, "Caregiver is required.")
      .required("Caregiver is required.");
  }

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      idNumber: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      timezone: "",
      password: "",
      caregiverId: [],
    },
    validationSchema: yup.object(schemaParams),
    onSubmit: async (values) => {
      try {
        const result = await registerClient(values);

        navigate("/client-add-success", {
          state: {
            user: result.user,
            tempPassword: result.tempPassword,
          },
        });
      } catch (error) {
        if (error.message.includes("already")) {
          formik.setFieldError(
            "email",
            "This email is already assigned to another client. Enter a different email."
          );
        } else {
          console.error("Error adding client:", error.message);
        }
      }
    },
  });

  return (
    <Layout isTouched={formik.dirty} showSideNav={false}>
      <div className="alerts-page-container">
        <div className="user-page-heading">
          <h1>Add new client</h1>
          <p>
            After you add the client, well generate a password they can use to
            sign in to My Compass. Then you can assign them to caregivers.
          </p>
        </div>
        <div className="add-client-container">
          <Box
            component="form"
            sx={{ "& > :not(style)": { m: 1, width: "100%" } }}
            noValidate
            autoComplete="off"
            onSubmit={formik.handleSubmit}
          >
            <h2 className="form-heading">
              <strong>Client Details</strong>
            </h2>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="firstName">First Name</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                  }
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                  fullWidth
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="lastName">Last Name</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  type="text"
                  id="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.lastName && Boolean(formik.errors.lastName)
                  }
                  helperText={formik.touched.lastName && formik.errors.lastName}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="idNumber">ID Number</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="idNumber"
                  type="text"
                  value={formik.values.idNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.idNumber && Boolean(formik.errors.idNumber)
                  }
                  helperText={formik.touched.idNumber && formik.errors.idNumber}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="email">
                  Email{" "}
                  <Tooltip title="The client will use this email to sign in to their My Compass account.">
                    <InfoIcon />
                  </Tooltip>
                </label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="phone">Phone Number</label>
              </Grid>
              <Grid item size={8}>
                <InputMask
                  mask="+19999999999"
                  value={formik.values.phone}
                  disabled={false}
                  maskChar=" "
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      id="phone"
                      type="tel"
                      placeholder="+1XXXXXXXXXX"
                      value={formik.values.phone}
                      error={
                        formik.touched.phone && Boolean(formik.errors.phone)
                      }
                      helperText={formik.touched.phone && formik.errors.phone}
                      fullWidth
                      required
                    />
                  )}
                </InputMask>
              </Grid>
            </Grid>

            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="address">Address</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="address"
                  type="text"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.address && Boolean(formik.errors.address)
                  }
                  helperText={formik.touched.address && formik.errors.address}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="city">City</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="city"
                  type="text"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="state">State</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="state"
                  name="state"
                  select
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                  fullWidth
                  required
                >
                  {US_STATES.map((stateName) => (
                    <MenuItem key={stateName} value={stateName}>
                      {stateName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="zipCode">ZIP code</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="zipCode"
                  type="text"
                  value={formik.values.zipCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.zipCode && Boolean(formik.errors.zipCode)
                  }
                  helperText={formik.touched.zipCode && formik.errors.zipCode}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="timezone">Timezone</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="timezone"
                  name="timezone"
                  select
                  value={formik.values.timezone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.timezone && Boolean(formik.errors.timezone)
                  }
                  helperText={formik.touched.timezone && formik.errors.timezone}
                  fullWidth
                  required
                >
                  {US_TIMEZONES.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {currentUser?.isSuperAdmin && (
              <TextField
                id="caregiverId"
                name="caregiverId"
                label="Caregiver"
                select
                value={formik.values.caregiverId}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.caregiverId &&
                  Boolean(formik.errors.caregiverId)
                }
                helperText={
                  formik.touched.caregiverId && formik.errors.caregiverId
                }
                fullWidth
                SelectProps={{
                  multiple: true,
                  onChange: formik.handleChange,
                }}
              >
                {caregivers.map((caregiver) => (
                  <MenuItem key={caregiver.id} value={caregiver.id.toString()}>
                    {caregiver.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box></Box>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                className="activity-action-btn"
                onClick={() => {
                  if (formik.dirty) {
                    setIsLeaveModalOpen(true);
                  } else {
                    navigate(-1);
                  }
                }}
              >
                <Typography textTransform={"capitalize"}>Cancel</Typography>
              </Button>
              <Button
                variant="outlined"
                className="activity-action-btn"
                onClick={formik.handleSubmit}
              >
                <Typography textTransform={"capitalize"}>Save</Typography>
              </Button>
            </Box>
          </Box>
        </div>
      </div>
      <LeaveWithoutSavingModal
        isOpen={isLeaveModalOpen}
        handleClose={() => setIsLeaveModalOpen(false)}
        handleLeave={() => navigate(-1)}
      />
    </Layout>
  );
};

export default AddClientPage;
